import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  HeartIcon,
  UserIcon,
  UsersIcon,
  CogIcon,
  DocumentTextIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  AcademicCapIcon,
  BanknotesIcon,
  ClockIcon,
  ClipboardDocumentListIcon,
  ChatBubbleBottomCenterTextIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

const AdminParametrage = () => {
  const navigate = useNavigate();

  const parametres = [
    {
      id: 'utilisateurs',
      title: 'Utilisateurs',
      description: 'Gérer les comptes utilisateurs et leurs droits d\'accès',
      icon: UserIcon,
      color: 'bg-blue-600',
      route: '/users'
    },
    {
      id: 'produits',
      title: 'Produits',
      description: 'Gérer les produits d\'assurance (Santé, Épargne, Prêts)',
      icon: BanknotesIcon,
      color: 'bg-green-600',
      route: '/admin/offers'
    },
    {
      id: 'situation-matrimoniale',
      title: 'Situation Matrimoniale',
      description: 'Gérer les statuts matrimoniaux (Célibataire, Marié(e), Divorcé(e), Veuf/Veuve)',
      icon: HeartIcon,
      color: 'bg-pink-500',
      route: '/admin/parametrage/situation-matrimoniale'
    },
    {
      id: 'statuts',
      title: 'Statuts',
      description: 'Gérer les statuts des utilisateurs et mutualistes',
      icon: UserIcon,
      color: 'bg-blue-500',
      route: '/admin/parametrage/statuts'
    },
    {
      id: 'lien-parente',
      title: 'Liens de Parenté',
      description: 'Gérer les relations familiales (Conjoint, Enfant, Parent, etc.)',
      icon: UsersIcon,
      color: 'bg-green-500',
      route: '/admin/parametrage/liens-parente'
    },
    {
      id: 'fonctions',
      title: 'Fonctions',
      description: 'Gérer les fonctions professionnelles',
      icon: AcademicCapIcon,
      color: 'bg-purple-500',
      route: '/admin/parametrage/fonctions'
    },
    {
      id: 'entreprises',
      title: 'Entreprises',
      description: 'Gérer la liste des entreprises partenaires',
      icon: BuildingOfficeIcon,
      color: 'bg-indigo-500',
      route: '/admin/parametrage/entreprises'
    },
    {
      id: 'localites',
      title: 'Localités',
      description: 'Gérer les villes, communes et quartiers',
      icon: MapPinIcon,
      color: 'bg-yellow-500',
      route: '/admin/parametrage/localites'
    },
    {
      id: 'types-documents',
      title: 'Types de Documents',
      description: 'Gérer les types de pièces d\'identité',
      icon: DocumentTextIcon,
      color: 'bg-red-500',
      route: '/admin/parametrage/types-documents'
    },
    {
      id: 'types-demande',
      title: 'Types de Demande',
      description: 'Gérer les types de demandes (Résiliation, Suspension, etc.)',
      icon: ClipboardDocumentListIcon,
      color: 'bg-orange-500',
      route: '/admin/parametrage/request-types'
    },
    {
      id: 'motifs',
      title: 'Motifs',
      description: 'Gérer les motifs de demande et de rejet',
      icon: ChatBubbleBottomCenterTextIcon,
      color: 'bg-cyan-500',
      route: '/admin/parametrage/reasons'
    },
    {
      id: 'taches-planifiees',
      title: 'Tâches planifiées',
      description: 'Gérer les tâches automatiques et jobs programmés',
      icon: ClockIcon,
      color: 'bg-indigo-600',
      route: '/admin/cron-jobs'
    },
    {
      id: 'reprise-donnees',
      title: 'Reprise de données',
      description: 'Outils d\'import et de recalcul (bulk payment V2, recalcul global épargne FSR)',
      icon: ArrowPathIcon,
      color: 'bg-amber-600',
      route: '/admin/parametrage/reprise-donnees'
    },
    {
      id: 'parametres-systeme',
      title: 'Paramètres Système',
      description: 'Configuration générale du système',
      icon: CogIcon,
      color: 'bg-gray-500',
      route: '/admin/parametrage/systeme'
    }
  ];

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="bg-white rounded-xl shadow-card p-6">
        <h1 className="text-2xl font-bold text-primary-900 mb-2">
          Paramétrage
        </h1>
        <p className="text-gray-600">
          Gérez les données de référence et la configuration du système.
        </p>
      </div>

      {/* Grille des paramètres */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {parametres.map((parametre) => {
          const IconComponent = parametre.icon;
          
          return (
            <div
              key={parametre.id}
              onClick={() => navigate(parametre.route)}
              className="bg-white rounded-xl shadow-card p-6 cursor-pointer hover:shadow-lg transition-shadow duration-200 border border-gray-200 hover:border-primary-300"
            >
              <div className="flex flex-col items-center text-center space-y-4">
                {/* Icône */}
                <div className={`${parametre.color} p-4 rounded-full`}>
                  <IconComponent className="h-8 w-8 text-white" />
                </div>
                
                {/* Titre */}
                <h3 className="text-lg font-semibold text-gray-900">
                  {parametre.title}
                </h3>
                
                
                {/* Bouton d'action */}
                <button className="w-full mt-4 px-4 py-2 bg-primary-50 text-primary-700 rounded-lg hover:bg-primary-100 transition-colors duration-200 text-sm font-medium">
                  Gérer
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Informations supplémentaires */}
      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg">
        <div className="flex">
          <div className="flex-shrink-0">
            <CogIcon className="h-5 w-5 text-blue-400" />
          </div>
          <div className="ml-3">
            <p className="text-sm text-blue-700">
              <span className="font-medium">Conseil :</span>
              {' '}Les paramètres de référence sont utilisés dans tout le système. 
              Assurez-vous de bien configurer ces données avant de créer des mutualistes.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminParametrage;
